/**
 * clarity.ts
 * ──────────────────────────────────────────────────────────────
 * Microsoft Clarity — session replay + heatmaps + rage-click detection.
 *
 * Activates ONLY when VITE_CLARITY_PROJECT_ID is set at build time. Blank
 * → no SDK init, no bundle impact beyond the tree-shaken import.
 *
 * Privacy rules for this project:
 *   - Admin, teacher, and center routes auto-stop recording (those screens show student
 *     PII like names, emails, phones). See stopClarityForAdminRoutes() below.
 *   - Student name / email / phone / answer inputs are auto-masked by Clarity
 *     defaults. Custom-typed content (tutor chat, WhatsApp numbers) can be
 *     masked per-element by adding `data-clarity-mask="true"` to the node.
 *   - We `identify()` with the opaque student id only — never name / email.
 *     Matches the rule applied across Sentry + PostHog.
 *
 * Loaded lazily, for the same reason as analytics.ts: session replay is
 * worth nothing if the student never got a screen to look at. The SDK is
 * imported inside initClarity() so it stays out of the critical path.
 */
import type ClarityT from '@microsoft/clarity'

const projectId = import.meta.env.VITE_CLARITY_PROJECT_ID ?? ''

let client: typeof ClarityT | null = null
let initialized = false

export function initClarity(): Promise<void> {
  if (!projectId || initialized) return Promise.resolve()

  // Don't record admin / teacher screens. Any route starting with these
  // prefixes skips init entirely — students never see them anyway, and
  // checking before the import means the SDK is never even fetched there.
  if (isStaffRoute(window.location.pathname)) return Promise.resolve()

  return import('@microsoft/clarity')
    .then(({ default: Clarity }) => {
      Clarity.init(projectId)
      client = Clarity
      initialized = true
    })
    .catch(() => {
      // Blocked, offline, or SDK init failed. Never break the app over it.
    })
}

/**
 * Tag the current session with the student id so replays can be grouped by
 * user cohort + cross-referenced with PostHog / Sentry events. Only the
 * opaque id is passed. Call after login, in sync with the Sentry + PostHog
 * identify hooks in useAuth.
 */
export function identifyClarity(studentId: string | null): void {
  if (!initialized || !client || !studentId) return
  try {
    // Clarity.identify signature: (customId, sessionId?, pageId?, userHint?)
    client.identify(studentId)
  } catch {}
}

/**
 * Stop recording during staff routes. Called by a React effect in
 * AdminDashboardPage + TeacherDashboardPage on mount → stop; on unmount → nothing
 * (next non-staff pageview will trigger a fresh session on the next init call).
 *
 * Also consulted inside initClarity() so a hard-reload on an admin URL skips
 * the SDK entirely.
 */
export function stopClarity(): void {
  if (!initialized || !client) return
  try {
    client.upgrade('staff_route_entered')  // tag before stopping, useful in dashboard
  } catch {}
  // Clarity SDK doesn't expose a clean stop; next best thing is to mark the
  // session upgraded + let the navigation away from /admin terminate it naturally.
  initialized = false
}

function isStaffRoute(pathname: string): boolean {
  return pathname.startsWith('/admin')
    || pathname.startsWith('/teacher')
    || pathname.startsWith('/organization')
}

export const isClarityEnabled = !!projectId
